// The Terminal command line's two stateful helpers (#4398, epic #4391) —
// history browsing and Tab-completion cycling — pulled out of
// `CommandLine.tsx` so that component stays under `scripts/size-budget.ts`'s
// 120-line function ceiling. Both drive the SAME input value the component
// owns (`value`/`setValue`); neither renders anything.

import { type Accessor, createSignal, type Setter } from 'solid-js';
import { z } from 'zod';
import { readLocalPref, writeLocalPref } from '../local-pref';
import { COMMAND_HISTORY_KEY } from './command-history-store';
import { completeCommandLine } from './completion';

const COMMAND_HISTORY_MAX = 100;
const CommandHistorySchema = z.array(z.string());

/** Push `line` onto `history`, deduping only a direct repeat of the last entry
 *  (typing the same lookup twice in a row shouldn't grow the list), bounded
 *  to the newest `COMMAND_HISTORY_MAX`. */
function pushHistory(history: readonly string[], line: string): readonly string[] {
  const appended = history[history.length - 1] === line ? history : [...history, line];
  return appended.length > COMMAND_HISTORY_MAX
    ? appended.slice(appended.length - COMMAND_HISTORY_MAX)
    : appended;
}

export type CommandHistory = {
  /** Step to the previous (older) entry — ArrowUp. */
  readonly prev: () => void;
  /** Step to the next (newer) entry, back to the draft past the newest — ArrowDown. */
  readonly next: () => void;
  /** Stop browsing (the draft stays in the box). */
  readonly leave: () => void;
  /** Record a submitted line, persist it, and return the new list (for `history`). */
  readonly record: (line: string) => readonly string[];
};

/** Persisted command history (`dz-terminal-command-history`, the `local-pref` seam). */
export function useCommandHistory(
  value: Accessor<string>,
  setValue: Setter<string>,
): CommandHistory {
  const [history, setHistory] = createSignal<readonly string[]>(
    readLocalPref(COMMAND_HISTORY_KEY, CommandHistorySchema, []),
  );
  // `null` means "not browsing history — showing the live draft". `draft`
  // holds what was being typed before an ArrowUp started browsing, so
  // ArrowDown back past the newest entry restores it rather than an empty box.
  const [index, setIndex] = createSignal<number | null>(null);
  let draft = '';

  return {
    prev: () => {
      const rows = history();
      if (rows.length === 0) return;
      const idx = index();
      const at = idx === null ? rows.length - 1 : Math.max(0, idx - 1);
      if (idx === null) draft = value();
      setIndex(at);
      setValue(rows[at] ?? '');
    },
    next: () => {
      const idx = index();
      if (idx === null) return;
      const rows = history();
      if (idx + 1 >= rows.length) {
        setIndex(null);
        setValue(draft);
        return;
      }
      setIndex(idx + 1);
      setValue(rows[idx + 1] ?? '');
    },
    leave: () => setIndex(null),
    record: (line) => {
      const nextHistory = pushHistory(history(), line);
      setHistory(nextHistory);
      writeLocalPref(COMMAND_HISTORY_KEY, nextHistory);
      setIndex(null);
      draft = '';
      return nextHistory;
    },
  };
}

/**
 * Tab completion over `completion.ts`. Repeated Tab cycles through the
 * candidates while the input still holds what the previous Tab put there; any
 * edit starts over. A lone match gets a trailing space, so the next Tab has an
 * empty word and falls through to moving focus. Returns whether it completed
 * anything — the keymap swallows Tab only then, so this is never a focus trap.
 */
export function useCompletion(value: Accessor<string>, setValue: Setter<string>): () => boolean {
  let cycle: {
    readonly prefix: string;
    readonly candidates: readonly string[];
    index: number;
    applied: string;
  } | null = null;

  return () => {
    if (cycle === null || value() !== cycle.applied || cycle.candidates.length < 2) {
      const found = completeCommandLine(value());
      if (found.candidates.length === 0) {
        cycle = null;
        return false;
      }
      cycle = {
        prefix: value().slice(0, found.start),
        candidates: found.candidates,
        index: 0,
        applied: '',
      };
    } else {
      cycle.index = (cycle.index + 1) % cycle.candidates.length;
    }
    const word = cycle.candidates[cycle.index] ?? '';
    const lone = cycle.candidates.length === 1 && !word.endsWith('=');
    cycle.applied = `${cycle.prefix}${word}${lone ? ' ' : ''}`;
    setValue(cycle.applied);
    return true;
  };
}
